import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, session, type CurrentUser, type LoginPayload } from '@/core';
import * as authApi from '@/api/auth';

/**
 * D-3（契约 §2）：423 的**确定重试时刻**取后端声明 ⇒ epoch ms。
 * 优先级：① 体 `data.lockedUntil`（t80：第 5 次失败那一刻 + 30min，UTC）／`unlockAt`／`lockExpireAt`；
 * ② 体 `retryAfterSeconds`／`retryAfter`（秒 ⇒ 换算绝对时刻）；③ 皆缺 ⇒ 名义兜底窗口
 * （`ACCOUNT_LOCK_MINUTES`，今日只在 mock 桩档出现，⛔ 非权威）。
 * ⚠ 限制：`Retry-After` **响应头**在本层不可达（`ApiError` 不持有 response，`core/http/client.ts`
 * 不在本切片写域）⇒ 头值只能经体字段参与，已登记在证据文 `dbf-frontend-session.md`。
 */
function serverUnlockAt(err: ApiError): number {
  const payload =
    err.payload && typeof err.payload === 'object'
      ? (err.payload as Record<string, unknown>)
      : ({} as Record<string, unknown>);
  const declared =
    session.toEpochMs(payload.lockedUntil) || session.toEpochMs(payload.unlockAt) || session.toEpochMs(payload.lockExpireAt);
  if (declared > 0) return declared;
  const seconds = Number(payload.retryAfterSeconds ?? payload.retryAfter ?? 0);
  if (Number.isFinite(seconds) && seconds > 0) return Date.now() + seconds * 1000;
  return Date.now() + session.ACCOUNT_LOCK_MINUTES * 60 * 1000;
}

/** auth store — 登录态 / 令牌 / 会话绝对截止（后端 expiresAt 驱动，D-1） / 登录失败锁定倒计时（D-3） */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(session.getSessionUser());
  const expiresAt = ref<number>(session.sessionExpiresAt());
  const loading = ref(false);
  const lockedUntil = ref<number>(session.lockedUntil());
  const failCount = ref(0);

  const isAuthenticated = computed(() => !!user.value && !!session.getAccessToken());
  const isSuperAdmin = computed(() => user.value?.roleCode === 'SUPER_ADMIN');
  const permissions = computed(() => user.value?.permissions ?? []);

  const lockRemainingSeconds = computed(() => {
    const left = lockedUntil.value - Date.now();
    return left > 0 ? Math.ceil(left / 1000) : 0;
  });

  async function login(payload: LoginPayload): Promise<CurrentUser> {
    loading.value = true;
    try {
      const result = await authApi.login(payload);
      // D-1：只把后端给出的绝对截止时刻落盘（`sessionExpiresAtMs`），⛔ 不再传「剩余时长」
      session.saveSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAtMs: result.sessionExpiresAtMs,
        user: result.user,
      });
      session.resetLoginFailure();
      user.value = result.user;
      expiresAt.value = session.sessionExpiresAt();
      failCount.value = 0;
      lockedUntil.value = 0;
      return result.user;
    } catch (err) {
      // §6.0-④：423 由服务端权威判定，本地必须同步锁定窗口，否则刷新即解锁、继续重试只会延长
      const locked = err instanceof ApiError && err.isAccountLocked;
      const lockedUntilAtMs = locked ? serverUnlockAt(err) : undefined;
      const info = session.recordLoginFailure(lockedUntilAtMs);
      failCount.value = info.count;
      lockedUntil.value = info.lockedUntil;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout(callApi = true): Promise<void> {
    if (callApi) {
      try {
        await authApi.logout();
      } catch {
        /* 登出失败不阻塞本地清理 */
      }
    }
    session.clearSession();
    user.value = null;
    expiresAt.value = 0;
    snapshotState.value = 'pending';
  }

  function forceLogout(): void {
    session.clearSession();
    user.value = null;
    expiresAt.value = 0;
    snapshotState.value = 'pending';
    const redirect = encodeURIComponent(window.location.hash.replace(/^#/, '') || '/dashboard');
    window.location.hash = `#/login?redirect=${redirect}&reason=timeout`;
  }

  function restore(): CurrentUser | null {
    const stored = session.getSessionUser();
    user.value = stored;
    expiresAt.value = session.sessionExpiresAt();
    return stored;
  }

  /** §6.2-7 快照同步状态：'pending' 未发起 / 'ok' 已按 profile 重建 / 'stale' 端点不可用（沿用 interim 快照） */
  const snapshotState = ref<'pending' | 'ok' | 'stale'>('pending');
  const snapshotError = ref('');

  /**
   * §3.5-② / §6.2-7：刷新页面后**必须**用 `GET /auth/profile` 重建权限态，
   * 不得沿用 localStorage 旧快照（`permVersion` 可能已变）。
   * 端点不可用时不静默假装已刷新：保留登录时服务端签发的 interim 快照并记原因，供界面提示「权限待确认」。
   */
  async function syncProfile(): Promise<void> {
    if (snapshotState.value === 'ok' || !session.getAccessToken()) return;
    try {
      const fresh = await authApi.fetchProfile();
      // 跨端登录隔离：profile 可能来自旧后端（无 console 键），保留登录时从令牌带下来的归属，
      // 否则一次刷新就把控制台自检判据抹成 null。
      const merged = { ...fresh, console: fresh.console ?? user.value?.console ?? null };
      user.value = merged;
      session.saveSessionUser(merged);
      expiresAt.value = session.sessionExpiresAt();
      snapshotState.value = 'ok';
      snapshotError.value = '';
      // permVersion 变化时无需手动失效缓存：permission store 直接读 user.permissions，已是新值
    } catch (err) {
      snapshotState.value = 'stale';
      snapshotError.value = err instanceof Error ? err.message : String(err);
    }
  }

  /** 供界面/排查判断当前权限态是否已由 §6.2-7 重建 */
  const snapshotFresh = computed(() => snapshotState.value === 'ok');

  return {
    user,
    expiresAt,
    loading,
    failCount,
    lockedUntil,
    lockRemainingSeconds,
    isAuthenticated,
    isSuperAdmin,
    permissions,
    login,
    logout,
    forceLogout,
    restore,
    syncProfile,
    snapshotState,
    snapshotError,
    snapshotFresh,
  };
});
